/**
 * Product API Service
 */

import { GALLERY_CONFIG } from '../../gallery-config.js';

/**
 * Fetch products from Shopify collection API
 * @param {string} collectionHandle - The handle of the collection to fetch from (defaults to 'all')
 * @returns {Promise<Array>} Array of raw product data
 */
export async function fetchCollectionProducts(collectionHandle = 'all') {
  const controller = new AbortController();
  const timeout_id = setTimeout(() => controller.abort(), GALLERY_CONFIG.PERFORMANCE.FETCH_TIMEOUT);

  try {
    // Build dynamic endpoint based on collection handle
    const endpoint = `/collections/${collectionHandle}/products.json`;
    const response = await window.fetch(`${endpoint}?limit=${GALLERY_CONFIG.API.MAX_PRODUCTS_PER_REQUEST}`, {
      signal: controller.signal
    });

    clearTimeout(timeout_id);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();

    if (!data.products || !Array.isArray(data.products)) {
      throw new Error('Invalid product data received');
    }

    return data.products;
  } catch (error) {
    clearTimeout(timeout_id);
    throw error;
  }
}

/**
 * Get the default variant from a list of variants
 * @param {Array} variants - Array of variant objects
 * @param {Object} variant_manager - Optional variant manager for advanced selection
 * @param {Array} priority - Optional priority array for size selection
 * @returns {Object|null} Default variant or first available variant
 */
export function getDefaultVariant(variants, variant_manager = null, priority = null) {
  if (!variants || variants.length === 0) {
    return null;
  }

  // If advanced selection is available, use it
  if (variant_manager && priority) {
    for (const priority_size of priority) {
      // Try premium configuration first
      const premium_variant = variant_manager.findPremiumVariant(variants, priority_size);
      if (premium_variant) return premium_variant;

      // Try size variant with SKU matching
      const size_variant = variant_manager.findVariantBySku(variants, priority_size);
      if (size_variant) return size_variant;
    }
  }

  // Fallback to simple selection
  return variants.find(v => v.available) || variants[0];
}

/**
 * Transform Shopify product data for gallery use
 * @param {Object} shopify_product - Raw product data from Shopify API
 * @returns {Object|null} Normalized product object
 */
export function transformProduct(shopify_product) {
  try {
    if (!shopify_product || typeof shopify_product !== 'object') {
      throw new Error('Invalid product data');
    }

    let exhibition_variant = shopify_product.variants?.find(v =>
      v.option1 === 'Exhibition' && v.option2 === 'Black' && v.option3 === 'Border'
    );

    if (!exhibition_variant) {
      exhibition_variant = shopify_product.variants?.find(v =>
        v.option1 === 'Exhibition' || v.option2 === 'Exhibition' || v.option3 === 'Exhibition'
      );
    }

    const default_variant = exhibition_variant || getDefaultVariant(shopify_product.variants, null, null);

    const base_image_source = default_variant?.featured_image?.src ||
                             default_variant?.image?.src ||
                             shopify_product.featured_image?.src ||
                             null;

    return {
      id: shopify_product.id || null,
      handle: shopify_product.handle || '',
      title: shopify_product.title || '',
      vendor: shopify_product.vendor || '',
      artist: shopify_product.metafields?.accentuate?.artist || shopify_product.vendor || 'Unknown Artist',
      _base_image_source: base_image_source,

      get original_image_url() {
        return this._base_image_source;
      },

      get original_thumbnail_url() {
        return this._base_image_source ? `${this._base_image_source.split('?')[0]}?width=88` : null;
      },

      base_price: default_variant?.price || 0,

      get _urls() {
        const store = Alpine.store('galleryCore');
        return store?.getArtworkUrls ? store.getArtworkUrls(this) : null;
      },

      get image_url() {
        return this._urls?.display || this._base_image_source;
      },

      get thumbnail_url() {
        return this._urls?.thumbnail ||
               (this._base_image_source ? `${this._base_image_source.split('?')[0]}?width=160` : null);
      },

      get horizontal_image_url() {
        return this._urls?.horizontal ||
               (this._base_image_source ? `${this._base_image_source.split('?')[0]}?width=872` : null);
      },

      get vertical_image_url() {
        return this._urls?.vertical ||
               (this._base_image_source ? `${this._base_image_source.split('?')[0]}?width=630` : null);
      },


      variants: shopify_product.variants.map(variant => ({
        id: variant.id,
        title: variant.title,
        price: variant.price,
        compare_at_price: variant.compare_at_price,
        available: variant.available,
        option1: variant.option1 || variant.options?.[0] || null,
        option2: variant.option2 || variant.options?.[1] || null,
        option3: variant.option3 || variant.options?.[2] || 'Border',
        featured_image: variant.featured_image ? {
          src: variant.featured_image.src,
          alt: variant.featured_image.alt
        } : null,
        sku: variant.sku,
        barcode: variant.barcode
      })),

      selected_variant: null,
      display_variant: default_variant || null,
      variant_selected: false,

      options: shopify_product.options,
      options_with_values: shopify_product.options?.map((option, index) => ({
        name: option.name,
        position: option.position,
        values: option.values
      })) || [],

      available: shopify_product.available !== false,
      tags: shopify_product.tags || [],
      price_min: shopify_product.price_min,
      price_max: shopify_product.price_max
    };
  } catch (error) {
    console.warn('Failed to transform product:', error);
    return null;
  }
}

/**
 * Load and transform collection products
 * @param {string} collectionHandle - The handle of the collection to fetch from (defaults to 'all')
 * @returns {Promise<Array>} Array of transformed product objects
 */
export async function loadAndTransformProducts(collectionHandle = 'all') {
  const raw_products = await fetchCollectionProducts(collectionHandle);
  return raw_products
    .map(product => transformProduct(product))
    .filter(product => product !== null);
}